
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, User, FileText } from 'lucide-react';
import { ScoutCalendarEvent } from '@/hooks/useScoutCalendarEvents';

interface ScoutEventDetailsModalProps {
  event: ScoutCalendarEvent | null;
  open: boolean;
  onClose: () => void;
}

export const ScoutEventDetailsModal: React.FC<ScoutEventDetailsModalProps> = ({
  event,
  open,
  onClose
}) => {
  if (!event) return null;

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return null;
    try {
      return new Date(`2024-01-01T${timeStr}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return timeStr;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'scheduled':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getTypeDisplay = (type: string) => {
    switch (type) {
      case 'assigned_task':
        return 'Assigned Task';
      case 'personal_task':
        return 'Personal Event';
      default:
        return 'Event';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {event.title}
            {event.type === 'assigned_task' && (
              <Badge 
                variant="outline" 
                style={{ 
                  backgroundColor: event.athleteColor + '20',
                  borderColor: event.athleteColor,
                  color: event.athleteColor
                }}
              >
                Assigned Task
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {getTypeDisplay(event.type)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-gray-500 mr-2" />
            <div>
              <div>{formatDate(event.date)}</div>
              {event.time && (
                <div className="text-gray-600">
                  at {formatTime(event.time)}
                </div>
              )}
            </div>
          </div>

          {event.athleteName && (
            <div className="flex items-center">
              <User className="h-5 w-5 text-gray-500 mr-2" />
              <div>
                <span className="font-medium">{event.athleteName}</span>
                <div className="text-sm text-gray-600">Assigned to athlete</div>
              </div>
            </div>
          )}

          {event.location && (
            <div className="flex items-center">
              <MapPin className="h-5 w-5 text-gray-500 mr-2" />
              <div className="text-gray-700">
                {event.location}
              </div>
            </div>
          )}

          {event.description && (
            <div className="flex items-start">
              <FileText className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
              <div className="text-gray-700">
                {event.description}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Badge variant={getStatusColor(event.status)}>
              {event.status}
            </Badge>
            <Badge variant="outline">
              {getTypeDisplay(event.type)}
            </Badge>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
